import React, { useEffect, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';

import { CHART_FONT, CHART_INK, CHART_SURFACE, categoricalColor, tooltip } from './chartTheme';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels, annotationPlugin);

const WaterUsageChart = ({ data, unit, totalInitConsumption, showLegend = false, title = '', onLegendUpdate, colorOffset = 0 }) => {
    const labels = Object.keys(data);
    const values = Object.values(data);
    const total = Math.round(values.reduce((acc, value) => acc + value, 0));

    // Format total with spaces between thousands
    const formattedTotal = total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    const chartData = {
        labels: labels,
        datasets: [
            {
                label: 'Consommation d\'eau par usage',
                data: values,
                // Palette du thème (chartTheme.js) plutôt que l'arc-en-ciel par
                // défaut de chart.js : ce donut voisine avec les autres graphes
                // de la page, et douze teintes sans rapport se voyaient.
                // `colorOffset` : le second anneau d'une page commande &
                // contrôle reprend la palette là où le premier l'a laissée.
                backgroundColor: labels.map((_, i) => categoricalColor(colorOffset + i)),
                borderColor: CHART_SURFACE,
                borderWidth: 3,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        circumference: 360 * total / totalInitConsumption,
        plugins: {
            legend: {
                display: showLegend,
                position: 'bottom',
                fullSize: false,
                labels: {
                    usePointStyle: true,
                    generateLabels: (chart) => {
                        const dataset = chart.data.datasets[0];
                        return chart.data.labels.map((label, i) => {
                            const value = dataset.data[i];
                            const percentage = ((value / total) * 100).toFixed(1);
                            return {
                                text: `${label} (${percentage}%)`,
                                fillStyle: dataset.backgroundColor[i],
                                strokeStyle: dataset.backgroundColor[i],
                                lineWidth: 1,
                                hidden: false,
                                index: i,
                            };
                        });
                    },
                },
            },
            tooltip: tooltip({
                callbacks: {
                    label: function(context) {
                        const label = context.label || '';
                        const value = context.raw;
                        return ` ${label} : ${Math.round(value)} ${unit}`;
                    },
                },
            }),
            datalabels: { display: false },
            annotation: {
                annotations: {
                    totalLabel: {
                        type: 'label',
                        xValue: 0.5,
                        yValue: 0.5,
                        content: `${formattedTotal} ${unit}`,
                        font: {
                            family: CHART_FONT,
                            size: 30,
                            weight: 'bold',
                        },
                        color: CHART_INK,
                    },
                },
            },
        },
        cutout: '75%',
    };

    const previousLegendRef = useRef(null);

    useEffect(() => {
        if (onLegendUpdate) {
            const legendData = options.plugins.legend.labels.generateLabels({ data: chartData });
            const previousLegend = previousLegendRef.current;

            // Only update if the legend data has changed
            if (JSON.stringify(previousLegend) !== JSON.stringify(legendData)) {
                previousLegendRef.current = legendData;
                onLegendUpdate(legendData);
            }
        }
    }, [chartData, onLegendUpdate]);

    return (
        <div className="command-control-water-usage-chart-container">
            {title && <h3 className="water-usage-chart-title">{title}</h3>}
            <div className="command-control-chart-wrapper">
                <Doughnut data={chartData} options={options} />
            </div>
        </div>
    );
};

export default WaterUsageChart;